import { createSignal, type JSX } from 'solid-js';
import { useAdminAccount } from '../lib/admin';
import type { OperatorRefusal } from '../lib/operator-credential';
import { AdminOrgDetail } from './AdminOrgDetail';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// Loads one org's detail + members (`GET /v1/admin/accounts/:id`). Mounted only
// while a row is selected (the parent's <Show/>), so the query is created fresh
// per selection rather than toggled on/off inside the list container.
//
// QueryBoundary, not a bare Switch/Match: a hand-rolled `Match when={data}` with
// no error branch left a failed detail read stuck on the loading skeleton
// forever — the fallback covers "still loading" and "never going to load" alike.
//
// A write re-reads BOTH halves: this detail, and (through `onMutated`) the org
// list beside it, whose tier / seats / cap columns otherwise keep the pre-write
// row (plan 101 slice 17 row 17-11).
//
// THE REFUSALS LIVE HERE, not in the detail: the re-read rebuilds the detail
// under the QueryBoundary, and a refusal held inside it vanished with it. That
// holds for the operator-credential door's refusal too: a write that landed
// before the door refused another still re-reads. This loader is mounted per
// selected account (the panel's keyed Show), so the signals are per account by
// construction.

export function AdminOrgDetailLoader(props: {
  id: string;
  token: string | null;
  onMutated: () => void;
}): JSX.Element {
  const detail = useAdminAccount(() => props.id, props.token);
  const [refusal, setRefusal] = createSignal<string | null>(null);
  const [doorRefusal, setDoorRefusal] = createSignal<OperatorRefusal | null>(null);
  return (
    <QueryBoundary query={detail} loading={<Skeleton height="12rem" />}>
      {(account) => (
        <AdminOrgDetail
          account={account}
          token={props.token}
          refusal={refusal()}
          onRefusal={setRefusal}
          doorRefusal={doorRefusal()}
          onDoorRefusal={setDoorRefusal}
          onMutated={() => {
            void detail.refetch();
            props.onMutated();
          }}
        />
      )}
    </QueryBoundary>
  );
}
